import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

const PRO_KEY = "sa.clip_pro_token";

export default function ClipsProActivated() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data: d } = await api.get(`/clips/pro/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid" && d.pro_token) {
          localStorage.setItem(PRO_KEY, d.pro_token);
          setStatus("paid");
          return;
        }
      } catch (_) {
        if (attempts >= 8) {
          setStatus("error");
          return;
        }
      }
      if (attempts < 8) setTimeout(poll, 1800);
      else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; referenced values are stable module imports/constants
  }, [sessionId]);

  return (
    <div data-testid="clips-pro-activated-page" className="bg-white text-[#0a0a0a] min-h-screen flex flex-col">
      <Seo title="Clip Radar Pro — activated" description="Your subscription is being confirmed." />
      <Header />
      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-[900px] w-full px-6 lg:px-10 py-16 text-center">
          {status === "polling" && (
            <>
              <div className="overline text-[#52525b] mb-3">Confirming Stripe…</div>
              <h1 className="font-display font-extrabold text-5xl lg:text-7xl tracking-tighter">Almost there.</h1>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="overline text-[#ff3b30] mb-3">Activated ✓</div>
              <h1 className="font-display font-extrabold text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
                You&apos;re Pro.<br /><span className="text-[#ff3b30]">Every clip is yours.</span>
              </h1>
              <p className="mt-6 text-[#52525b]">
                Active until <b>{data?.expires_at?.slice(0, 10)}</b> · billed to <b>{data?.email}</b>
              </p>
              <button
                data-testid="pro-activated-cta"
                onClick={() => navigate("/clips")}
                className="hard-shadow inline-block mt-10 border border-black bg-black text-white px-6 py-3 overline"
              >
                Open Clip Radar →
              </button>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <div className="overline text-[#52525b] mb-3">Hmm</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter">
                Couldn&apos;t confirm your payment.
              </h1>
              <Link to="/clips" className="overline inline-block mt-8 border border-black px-5 py-2.5 hover:bg-black hover:text-white">
                ← Back to Clips
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
